import React, { useRef, useState, useEffect } from "react";
import Dropzone from "react-dropzone";
import axios from "axios";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

export default function App() {
  // --- FILE UPLOAD STATES ---
  const [file, setFile] = useState(null);
  const [output, setOutput] = useState("");
  const [status, setStatus] = useState("");

  // --- ROBOT SELECT ---
  const [robot, setRobot] = useState("OT-2");

  useEffect(() => {
    axios.get("http://localhost:5000/robot").then(res => {
      if (res.data?.robot) setRobot(res.data.robot); // "OT-2" or "Flex"
    });
  }, []);

  const setRobotOnServer = async (next) => {
    try {
      await axios.post("http://localhost:5000/robot", { robot: next });
      setRobot(next);
      setStatus(`Robot set to ${next}`);
    } catch {
      setStatus("Failed to update robot");
    }
  };

  // --- LOGS ---
  const [logMessages, setLogMessages] = useState([]);
  const logEndRef = useRef(null); // for autoscroll
  const logContainerRef = useRef(null); // for scrollable container

  // --- CAMERA & DETECTIONS ---
  const [showCamera, setShowCamera] = useState(false);
  const [detections, setDetections] = useState([]);

  // --- Camera selection ---
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState("");

  // --- Labware setup state ---
  const [predictedSetup, setPredictedSetup] = useState({});
  const [showUploader, setShowUploader] = useState(false);

  // Refs for video + hidden canvas
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // --- 1) Fetch live logs every 3 s ---
  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/get-logs`);
        setLogMessages(data.logs || []);
      } catch (err) {
        console.error("Error fetching logs:", err);
      }
    };
    fetchLogs();
    const iv = setInterval(fetchLogs, 3000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (logContainerRef.current && logEndRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logMessages]);

  // --- 2) When showCamera flips on, start webcam & send 1 frame / sec ---
  useEffect(() => {
  if (!showCamera) return;

  async function initCameras() {
    try {
      // Request permission once so labels are populated
      await navigator.mediaDevices.getUserMedia({ video: true });

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter((d) => d.kind === "videoinput");
      setCameras(videoInputs);

      if (videoInputs[0]) {
        setSelectedCamera(videoInputs[0].deviceId);
        }
      } catch (err) {
        console.error("Error accessing cameras:", err);
      }
    }

    initCameras();
  }, [showCamera]);

  useEffect(() => {
    if (!showCamera || !selectedCamera) return;
    let cancelled = false;
    let stream;

    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    
    const start = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: selectedCamera } },
        });

        if (!videoRef.current) return;
        videoRef.current.srcObject = stream;
        await videoRef.current.play();

        // Ensure canvas matches the video size
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = video.videoWidth || canvas.width;
        canvas.height = video.videoHeight || canvas.height;
        const ctx = canvas.getContext("2d");

        while (!cancelled) {
          // 1) Capture current frame
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          // 2) Convert to blob
          const blob = await new Promise((resolve) =>
            canvas.toBlob(resolve, "image/jpeg", 0.9)
          );
          if (!blob) {
            await sleep(1000);
            continue;
          }

          // 3) Send to backend
          setStatus("Vision model running...");   
          const form = new FormData();
          form.append("frame", blob, "frame.jpg");
          try {
            const res = await fetch(`${BACKEND_URL}/analyze-frame`, {
              method: "POST",
              body: form,
              // mode: "cors",
            });
            const data = await res.json();
            if (!cancelled && data?.detections) {
              setDetections(data.detections);
            }
            setStatus("Vision complete ✅");
          } catch (e) {
            setStatus("Vision failed ❌");
            console.error("Frame send error:", e);
          }
          // 4) Wait 1s AFTER completion before next run
          await sleep(1000);
        }
      } catch (e) {
        console.error("Webcam error:", e);
      }
    };
    start();
    return () => {
      cancelled = true;
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, [showCamera, selectedCamera]);

  // --- 2b) Fetch predicted setup after detections update ---
  useEffect(() => {
    if (detections.length === 0) return;

    setStatus("LLM predicting setup...");

    axios
      .get(`${BACKEND_URL}/labware-setup`)
      .then((res) => {
        if (res.data && res.data.labware_setup) {
          const { name, slots } = res.data.labware_setup;
          setPredictedSetup({ name, slots });
          setStatus("LLM setup prediction complete");
        }
      })
      
      .catch((err) => {
        setStatus("LLM setup prediction failed");
        console.error("Error fetching predicted setup:", err);
      });
  }, [detections]);

  // --- 4) File upload handlers ---
  const handleDrop = (files) => {
    setFile(files[0]);
    setStatus("");
    setOutput("");
  };

  const handleUpload = async () => {
    if (!file) {
      setStatus("Please upload a file first.");
      return;
    }
    setStatus("Processing...");

    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/run-script`,
        form,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 0 }
      );
      setOutput(data.output);
      setStatus("Processing completed!");
    } catch (err) {
      setOutput(err.response?.data?.error || "An error occurred.");
      setStatus("Failed to process the file.");
    }
  };

  return (
    <div className="App">
      <header className="App-header">

        <h1>Opentrons Labware Setup Checker</h1>
        <p style={{ maxWidth: "800px", margin: "0 auto 1.5rem", textAlign: "left"}}>
          Based on the webcam feed, this app predicts which protocol you are trying to set up and shows the expected labware arrangement. You may also upload a protocol file instead of letting the app predict the setup.  <br /><br />
          In real time, the app detects plates, racks, and tip boxes on the robot deck, compares them against the predicted configuration, and highlights any mismatches while streaming live logs.  <br /> <br />
          The live logs provide a running record of detection results and status updates, making it easy to track changes and troubleshoot setup issues. <br /> <br />
          Colored bounding boxes indicate item status: <strong style={{color: 'green'}}>green</strong> for correct placement, <strong style={{color: 'orange'}}>orange</strong> for items in the setup but placed in the wrong slot, and <strong style={{color: 'red'}}>red</strong> for items not included in the setup.
        </p>

        {/* --- Robot Toggle --- */}
        <div style={{ margin: "1rem 0" }}>
          <div className="robot-toggle">
            <button
              className={`robot-option ${robot === "OT-2" ? "active" : "inactive"}`}
              onClick={() => setRobotOnServer("OT-2")}
            >
              OT-2
            </button>
            <button
              className={`robot-option ${robot === "Flex" ? "active" : "inactive"}`}
              onClick={() => setRobotOnServer("Flex")}
            >
              Flex
            </button>
          </div>

        </div>


          <p className="status">{status}</p>

          {Object.keys(predictedSetup).length > 0 && (
            <div style={{ marginTop: "1rem" }}>
              <h3>Predicted Protocol: {predictedSetup.name} </h3>
              {Object.entries(predictedSetup.slots).map(([slot, label]) => (
                <div key={slot}>
                  <strong>slot {slot}:</strong> {label}
                </div>
              ))}
            </div>
          )}

        {/* --- Collapsible File Upload (after predicted protocol) --- */}
        <div style={{ marginTop: "1rem" }}>
          <button onClick={() => setShowUploader((s) => !s)}>
            Upload protocol instead
          </button>

          {showUploader && (
            <div style={{ marginTop: "1rem" }}>
              <h3>Drag and Drop Your Opentrons Protocol Script</h3>
              <Dropzone onDrop={handleDrop}>
                {({ getRootProps, getInputProps }) => (
                  <div {...getRootProps()} className="dropzone">
                    <input {...getInputProps()} />
                    <p>Drag & drop a file here, or click to select one</p>
                  </div>
                )}
              </Dropzone>

              {file && <p>Selected file: {file.name}</p>}

              <button onClick={handleUpload} className="process-button">
                Process File
              </button>

              {output && (
                <div>
                  <h4>Correct configuration:</h4>
                  {Object.entries(output).map(([k, v]) => (
                    <div key={k}>
                      <strong>slot {k}:</strong> {v}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* --- Live Logs --- */}
        {logMessages.length > 0 && (
          <div
            ref={logContainerRef}
            style={{
              marginTop: "1rem",
              textAlign: "left",
              maxHeight: "200px",
              overflowY: "auto",
              backgroundColor: "#fff",
              padding: "1rem",
              borderRadius: "8px",
              color: "#000",
              fontFamily: "monospace",
            }}
          >
            <h3>Live Log Output</h3>
            {logMessages.map((line, i) => {
              // Extract timestamp from start of line
              const match = line.match(/^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}),(\d{3})/);
              let localTime = "";
              if (match) {
                const utcDate = new Date(`${match[1]}.${match[2]}Z`); // force UTC
                localTime = utcDate.toLocaleTimeString([], { hour12: false }) + `.${match[2]}`;
              }
              const cleanedLine = line.replace(
                /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},\d{3} - INFO - /,
                ""
              );

              let color = "#000"; // default: black text

              if (cleanedLine.includes("[MISSING]")) color = "purple";
              else if (cleanedLine.includes("[CORRECT]")) color = "green";
              else if (cleanedLine.includes("[WRONG]") || line.includes("[WRONG_SLOT]")) color = "#d74e20ff";
              else if (cleanedLine.includes("[NOT_IN_SETUP]")) color = "#e91a0bff";

              return (
                <div key={i} style={{ color }}>
                  {localTime} {cleanedLine}
                </div>
              );
            })}
            <div ref={logEndRef} /> {/*Scroll target*/}
          </div>
        )}

        {/* --- Camera (no clicks UI) --- */}
        <div style={{ marginTop: "2rem" }}>
          <button
            onClick={() => {
              setShowCamera(true);
              setStatus("Camera feed shown below.");
            }}
          >
            Show Live Camera Feed
          </button>

          {showCamera && (
            <div
              className="camera-feed"
              style={{ position: "relative", display: "inline-block" }}
            >
              {showCamera && cameras.length > 0 && (
              <div 
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",  
                  gap: "0.5rem",
                  marginBottom: "1rem",
                  marginTop: "1rem",
                }}>
                <label style={{ fontWeight: "500" }}>Camera:</label>
                 <select
                    className="camera-select"
                    value={selectedCamera}
                    onChange={(e) => setSelectedCamera(e.target.value)}
                  >
                    {cameras.map((cam, i) => (
                      <option key={cam.deviceId} value={cam.deviceId}>
                        {cam.label || `Camera ${i + 1}`}
                      </option>
                    ))}
                  </select>
              </div>
            )}
              <video
                ref={videoRef}
                width={640}
                height={480}
                autoPlay
                muted
                style={{ border: "2px solid #ccc" }}
              />
              {detections.map((det, i) => {
                const { bbox, label, confidence, slot, match } = det;
                const borderColor =
                  match === "correct" ? "lime" :
                  match === "wrong_slot" ? "orange" :
                  "red";

                const [x1, y1, x2, y2] = bbox;

                if (!videoRef.current) return null;

                const videoRect = videoRef.current.getBoundingClientRect();
                const containerRect = videoRef.current.parentElement.getBoundingClientRect();

                // Scaling from natural video dimensions to rendered size
                const scaleX = videoRect.width / videoRef.current.videoWidth;
                const scaleY = videoRect.height / videoRef.current.videoHeight;

                // Offset to account for horizontal centering
                const offsetX = videoRect.left - containerRect.left;
                const offsetY = videoRect.top - containerRect.top;

                // Final positioned box
                const left = offsetX + x1 * scaleX;
                const top = offsetY + y1 * scaleY;
                const width = (x2 - x1) * scaleX;
                const height = (y2 - y1) * scaleY;

              return (
                <div
                  key={i}
                  style={{
                    position: "absolute",
                    border: `2px solid ${borderColor}`,
                    left: `${left}px`,
                    top: `${top}px`,
                    width: `${width}px`,
                    height: `${height}px`,
                    color: borderColor,
                    fontSize: "10px",
                    pointerEvents: "none",
                    zIndex: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",       
                    whiteSpace: "normal",      
                    overflow: "hidden",        

                  }}
                >
                  <span
                    style={{
                      background: "rgba(0,0,0,0.4)",  
                      padding: "2px 4px",
                      borderRadius: "4px",           
                      maxWidth: "100%",        
                      wordBreak: "break-word", 

                    }}
                  >
                    {label} ({slot}) — {(confidence * 100).toFixed(1)}%
                  </span>
                </div>

              );
              })}
              <canvas
                ref={canvasRef}
                width={640}
                height={480}
                style={{ display: "none" }}
              />
            </div>
          )}
        </div>
      </header>
    </div>
  );
}
